"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/app/lib/utils/cn";
import { getFlocktankUrl, getFlockworkerUrl } from "@/lib/utils/propertyUrls";

interface User {
  id: string;
  email: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  emailVerified: boolean;
  mfaEnabled: boolean;
  isAdmin?: boolean;
}

interface AuthNavProps {
  className?: string;
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}

function getInitials(name: string | null | undefined, email?: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return ((parts[0]?.charAt(0) ?? '') + (parts[parts.length - 1]?.charAt(0) ?? '')).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return "U";
}

function getDisplayName(name: string | null | undefined, email: string | null | undefined): string {
  if (name) return name.split(" ")[0] ?? name;
  if (email) return email.split("@")[0] ?? email;
  return "Account";
}

function getFullName(name: string | null | undefined, email: string | null | undefined): string {
  if (name) return name;
  if (email) return email.split("@")[0] ?? email;
  return "Account";
}

export default function AuthNav({ className, variant = "desktop", onNavigate }: AuthNavProps) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/session", { cache: "no-store" });
      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          setUser(data.user);
          setStatus("authenticated");
        } else {
          setUser(null);
          setStatus("unauthenticated");
        }
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    } catch {
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => {
    checkSession(); // eslint-disable-line react-hooks/set-state-in-effect -- fetch session on navigation
  }, [pathname, checkSession]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSignOut = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
      window.location.href = "/";
    } catch {
      window.location.href = "/";
    }
  };

  if (status === "loading") {
    return (
      <div 
        className={cn(
          "flex items-center gap-2 bg-white/90 dark:bg-(--dark-purple)/90 backdrop-blur-sm rounded-full px-2 py-1.5 lg:px-4 lg:py-2 shadow-md border border-(--purple)/10 min-h-[44px]",
          className
        )}
      >
        <div className="w-6 h-6 lg:w-4 lg:h-4 rounded-full bg-(--purple)/20 animate-pulse" />
        <span className="hidden lg:inline text-sm font-medium text-(--purple-accent)/50">Loading...</span>
      </div>
    );
  }

  if (status === "unauthenticated") {
    // Combined Sign Up/In button for both mobile and desktop
    // OAuth handles both registration and login in a single flow
    return (
      <Link
        href="/auth/sign-in"
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-1.5 lg:gap-2 bg-gradient-to-r from-(--brand) to-(--gold) rounded-full px-3 py-1.5 lg:px-5 lg:py-2 min-h-11 shadow-md hover:shadow-lg transition-all hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-(--gold) focus-visible:ring-offset-2",
          className
        )}
      >
        <svg className="w-4 h-4 text-(--dark-purple)" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
        <span className="text-sm lg:text-base font-bold text-(--dark-purple)">Sign Up/In</span>
      </Link>
    );
  }

  const initials = getInitials(user?.name, user?.email);
  const displayName = getDisplayName(user?.name, user?.email);
  const fullName = getFullName(user?.name, user?.email);

  if (variant === "mobile") {
    return (
      <div className={cn("relative", className)} ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-1.5 bg-white/90 dark:bg-(--dark-purple)/90 backdrop-blur-sm rounded-full pl-1.5 pr-2.5 py-1.5 shadow-md hover:shadow-lg transition-shadow min-h-[44px] border border-(--purple)/10"
          aria-expanded={isDropdownOpen}
          aria-haspopup="menu"
          aria-controls="user-menu-mobile"
          aria-label={`Account menu for ${fullName}`}
        >
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-(--gold) to-(--brand) flex items-center justify-center text-white text-xs font-bold shadow-inner">
            {initials}
          </div>
          <span className="text-sm font-semibold text-(--text-primary) dark:text-(--gold)">{displayName}</span>
          <svg
            className={cn("w-3 h-3 text-(--text-muted) dark:text-(--gold) transition-transform duration-200", isDropdownOpen && "rotate-180")}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isDropdownOpen && (
          <div 
            id="user-menu-mobile"
            role="menu"
            className="absolute top-full right-0 mt-2 bg-white dark:bg-(--dark-purple) rounded-xl shadow-2xl border border-(--purple)/10 min-w-[200px] z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-(--border-color) bg-(--purple)/5 dark:bg-(--purple)/20">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-(--text-headline) truncate">{fullName}</p>
                {user?.mfaEnabled && (
                  <span className="flex-shrink-0" title="2FA Enabled">
                    <svg className="w-3.5 h-3.5 text-(--teal)" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>
              <p className="text-xs text-(--text-muted) truncate">{user?.email}</p>
            </div>

            <div className="py-1">
              {user?.isAdmin && (
                <Link
                  href="/admin"
                  onClick={() => { setIsDropdownOpen(false); onNavigate?.(); }}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-(--gold)/10 transition-colors border-b border-(--border-color)"
                  role="menuitem"
                >
                  <svg className="w-4 h-4 text-(--gold)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-sm font-semibold text-(--gold)">Admin Dashboard</span>
                </Link>
              )}

              <Link
                href="/settings/profile"
                onClick={() => { setIsDropdownOpen(false); onNavigate?.(); }}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
                role="menuitem"
              >
                <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm font-medium text-(--text-primary)">My Profile</span>
              </Link>

              <Link
                href="/settings/security"
                onClick={() => { setIsDropdownOpen(false); onNavigate?.(); }}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
                role="menuitem"
              >
                <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-sm font-medium text-(--text-primary)">Security Settings</span>
              </Link>

              <Link
                href="/settings"
                onClick={() => { setIsDropdownOpen(false); onNavigate?.(); }}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
                role="menuitem"
              >
                <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-medium text-(--text-primary)">Account Settings</span>
              </Link>
            </div>

            <div className="border-t border-(--border-color)">
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-500/10 text-red-500 w-full transition-colors"
                role="menuitem"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center gap-2 bg-white/90 dark:bg-(--dark-purple)/90 backdrop-blur-sm rounded-full pl-2 pr-4 py-1.5 shadow-md border border-(--purple)/10 hover:shadow-lg transition-shadow min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--gold) focus-visible:ring-offset-2"
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
        aria-controls="user-menu-desktop"
        aria-label={`Account menu for ${fullName}`}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-(--gold) to-(--brand) flex items-center justify-center text-white text-xs font-bold shadow-inner">
          {initials}
        </div>
        <div className="flex flex-col items-start">
          <span className="text-sm md:text-base font-semibold text-(--text-primary) dark:text-(--gold)">
            {displayName}
          </span>
          <span className="text-xs text-(--text-muted) flex items-center gap-1">
            {user?.mfaEnabled ? (
              <>
                <svg className="w-3 h-3 text-(--teal)" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                2FA Enabled
              </>
            ) : !user?.emailVerified ? (
              <>
                <svg className="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Verify Email
              </>
            ) : (
              "Member"
            )}
          </span>
        </div>
        <svg
          className={cn("w-3.5 h-3.5 text-(--text-muted) dark:text-(--gold) transition-transform duration-200", isDropdownOpen && "rotate-180")}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isDropdownOpen && (
        <div 
          id="user-menu-desktop"
          role="menu"
          className="absolute top-full right-0 mt-2 bg-white dark:bg-(--dark-purple) rounded-xl shadow-2xl border border-(--purple)/10 min-w-[240px] z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-(--border-color) bg-(--purple)/5 dark:bg-(--purple)/20">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-(--text-headline)">{fullName}</p>
              {user?.mfaEnabled && (
                <span className="flex-shrink-0" title="2FA Enabled">
                  <svg className="w-4 h-4 text-(--teal)" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
            <p className="text-xs text-(--text-muted) truncate">{user?.email}</p>
          </div>

          <div className="py-1">
            {user?.isAdmin && (
              <Link
                href="/admin"
                onClick={() => setIsDropdownOpen(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-(--gold)/10 transition-colors border-b border-(--border-color)"
                role="menuitem"
              >
                <svg className="w-4 h-4 text-(--gold)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-semibold text-(--gold)">Admin Dashboard</span>
              </Link>
            )}

            <Link
              href="/settings/profile"
              onClick={() => setIsDropdownOpen(false)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
              role="menuitem"
            >
              <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm font-medium text-(--text-primary)">My Profile</span>
            </Link>

            <Link
              href="/settings/security"
              onClick={() => setIsDropdownOpen(false)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
              role="menuitem"
            >
              <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-sm font-medium text-(--text-primary)">Security Settings</span>
            </Link>

            <div className="px-4 py-2 border-t border-b border-(--border-color) my-1">
              <p className="text-xs font-semibold text-(--text-muted) uppercase tracking-wider mb-2">My Properties</p>
              <div className="space-y-1">
                <a
                  href={getFlocktankUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 py-1.5 text-sm text-(--text-secondary) hover:text-(--brand) transition-colors"
                >
                  <span className="w-5 h-5 rounded bg-(--gold)/20 flex items-center justify-center text-[10px] font-bold text-(--gold)">FT</span>
                  <span>FlockTank</span>
                  <span className="text-[10px] text-(--text-muted) ml-auto">Investing</span>
                </a>
                <a
                  href={getFlockworkerUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 py-1.5 text-sm text-(--text-secondary) hover:text-(--brand) transition-colors"
                >
                  <span className="w-5 h-5 rounded bg-(--teal)/20 flex items-center justify-center text-[10px] font-bold text-(--teal)">FW</span>
                  <span>FlockWorker</span>
                  <span className="text-[10px] text-(--text-muted) ml-auto">Jobs</span>
                </a>
              </div>
            </div>

            <Link
              href="/settings"
              onClick={() => setIsDropdownOpen(false)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
              role="menuitem"
            >
              <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-medium text-(--text-primary)">Account Settings</span>
            </Link>

            <Link
              href="/settings/emails"
              onClick={() => setIsDropdownOpen(false)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-(--purple)/5 dark:hover:bg-(--purple)/20 transition-colors"
              role="menuitem"
            >
              <svg className="w-4 h-4 text-(--purple-accent)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium text-(--text-primary)">Email Preferences</span>
            </Link>
          </div>

          <div className="border-t border-(--border-color)">
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 px-4 py-3 hover:bg-red-500/10 text-red-500 w-full transition-colors"
              role="menuitem"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
